import { getIndustries } from 'APIs/industries/industries';
import { Industry } from 'APIs/industries/industries.types';
import { getMerchantMarketplaceSettings, updateMerchantMarketplaceSettings } from 'APIs/marketplaces/marketplaces';
import {
  MerchantMarketplaceSettingsResponse,
  MerchantMarketplaceSettingsUpdateRequest,
} from 'APIs/marketplaces/merchant.marketplaces.settings.types';
import Spinner from 'Components/Spinner/Spinner';
import { merchantTaxCountries } from 'Constants/Constants';
import { Content, LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import MerchantSettingsForm from './MerchantSettingsForm';
import MerchantSettingsView from './MerchantSettingsView';
import { CountrySelect, EditMerchantSettings, MerchantSettingsHeader, MessageWrapper } from './styles';

const MerchantMarketplaceSettings = () => {
  const { marketplaceId } = useParams<{ marketplaceId: string; manage: string }>();
  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [merchantSettings, setMerchantSettings] = useState<MerchantMarketplaceSettingsResponse | null>(null);
  const [taxCountry, setTaxCountry] = useState<string>('AU');
  const [industries, setIndustries] = useState<Array<Industry>>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [updateErrorMsg, setUpdateErrorMsg] = useState<string>('');
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  const fetchMerchantSettings = useCallback(async () => {
    try {
      setIsFetching(true);
      const resp = await getMerchantMarketplaceSettings(apiBaseUri, marketplaceId, taxCountry);
      setMerchantSettings(resp);
    } catch (error) {
      setMerchantSettings(null);
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  }, [apiBaseUri, marketplaceId, taxCountry]);

  const fetchIndustries = useCallback(async () => {
    try {
      const indList = await getIndustries(apiAprilBaseUri);
      setIndustries(indList);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  }, [apiAprilBaseUri]);

  const updateMerchantSettings = useCallback(
    async (data: MerchantMarketplaceSettingsUpdateRequest): Promise<void> => {
      try {
        setIsUpdating(true);

        await updateMerchantMarketplaceSettings(apiBaseUri, marketplaceId, data);
        fetchMerchantSettings();
        setUpdateErrorMsg('');
        setShowUpdateModal(false);
        toast.success('Successfully updated merchant marketplace settings');
      } catch (error) {
        setUpdateErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsUpdating(false);
      }
    },
    [apiBaseUri, fetchMerchantSettings, marketplaceId],
  );

  const handleEditButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setUpdateErrorMsg('');
    setShowUpdateModal(true);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTaxCountry(e.target.value);
  };

  useEffect(() => {
    if (!marketplaceId) {
      return;
    }
    fetchMerchantSettings();
  }, [fetchMerchantSettings, marketplaceId]);

  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  return (
    <>
      <Content>
        <h3>Tax country</h3>
        <CountrySelect value={taxCountry} onChange={handleCountryChange}>
          {merchantTaxCountries.map((country) => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </CountrySelect>
        <LpBox>
          {isFetching && !merchantSettings ? (
            <MessageWrapper>
              <Spinner color="#0016D1" height="25px" width="25px" />
            </MessageWrapper>
          ) : (
            <>
              <MerchantSettingsHeader>
                <h3 className="mb-0">Settings</h3>

                <EditMerchantSettings onClick={handleEditButtonClick}>
                  {merchantSettings ? 'Edit' : 'Add'}
                </EditMerchantSettings>
              </MerchantSettingsHeader>

              {showUpdateModal && industries.length > 0 && (
                <MerchantSettingsForm
                  taxCountry={taxCountry}
                  merchantSettings={merchantSettings}
                  isLoading={isUpdating}
                  errorMsg={updateErrorMsg}
                  industries={industries}
                  updateMerchantSettings={updateMerchantSettings}
                  setShowUpdateModal={setShowUpdateModal}
                />
              )}

              <MerchantSettingsView merchantSettings={merchantSettings} industries={industries} />
            </>
          )}
        </LpBox>
      </Content>
    </>
  );
};

export default MerchantMarketplaceSettings;
